// マスタデータ API（categories / vendors / departments / items / banks / settings）。
// 2026-05-15 リファクタで src/api.ts から分離。
import { request } from './_request'

// === カテゴリ ===
export const categories = {
  list: () => request<import('../types').CategoriesData>('/categories'),
  listTrashed: () => request<any[]>('/categories?trashed=1'),
  create: (data: { name: string; type: string }) =>
    request('/categories', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: { id: string; name?: string; sortOrder?: number; updatedAt?: string }) =>
    request<{ ok: boolean; updatedAt?: string }>('/categories', { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string) =>
    request('/categories', { method: 'DELETE', body: JSON.stringify({ id }) }),
  restore: (id: string) =>
    request('/categories?action=restore', { method: 'POST', body: JSON.stringify({ id }) }),
}

// === 取引先 ===
export const vendors = {
  list: () => request<import('../types').Vendor[]>('/vendors'),
  listTrashed: () => request<any[]>('/vendors?trashed=1'),
  impact: (id: string) =>
    request<{ cells: number; reschedules: number; debts: number; recurring: number }>(`/vendors?id=${encodeURIComponent(id)}&impact=1`),
  create: (data: Partial<import('../types').Vendor>) =>
    request<any>('/vendors', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: Partial<import('../types').Vendor> & { updatedAt?: string }) =>
    request<{ ok: boolean; updatedAt?: string }>('/vendors', { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string) =>
    request('/vendors', { method: 'DELETE', body: JSON.stringify({ id }) }),
  restore: (id: string) =>
    request('/vendors?action=restore', { method: 'POST', body: JSON.stringify({ id }) }),
}

// === 部門（階層対応） ===
export const departments = {
  list: () => request<import('../types').Department[]>('/departments'),
  listTrashed: () => request<import('../types').Department[]>('/departments?trashed=1'),
  create: (data: { name: string; color?: string; parentId?: string | null }) =>
    request<{ id: string; level: number; sortOrder: number }>('/departments', {
      method: 'POST', body: JSON.stringify(data),
    }),
  update: (data: {
    id: string;
    name?: string;
    color?: string;
    sortOrder?: number;
    parentId?: string | null;
  }) => request<{ ok: boolean }>('/departments', {
    method: 'PUT', body: JSON.stringify(data),
  }),
  remove: (id: string) => request<{ ok: boolean }>('/departments', {
    method: 'DELETE', body: JSON.stringify({ id }),
  }),
}

// === 品目（フラットマスタ） ===
export const items = {
  list: () => request<import('../types').Item[]>('/items'),
  listTrashed: () => request<import('../types').Item[]>('/items?trashed=1'),
  create: (data: { name: string; color?: string; memo?: string }) =>
    request<{ id: string; sortOrder: number }>('/items', {
      method: 'POST', body: JSON.stringify(data),
    }),
  update: (data: { id: string; name?: string; color?: string; memo?: string; sortOrder?: number }) =>
    request<{ ok: boolean }>('/items', {
      method: 'PUT', body: JSON.stringify(data),
    }),
  remove: (id: string) =>
    request<{ ok: boolean }>('/items', {
      method: 'DELETE', body: JSON.stringify({ id }),
    }),
  restore: (id: string) =>
    request<{ ok: boolean }>('/items?action=restore', {
      method: 'POST', body: JSON.stringify({ id }),
    }),
}

// === 銀行口座 ===
export const banks = {
  list: (from?: string, to?: string) => {
    const params = new URLSearchParams()
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    return request<import('../types').BanksData>(`/banks?${params}`)
  },
  listTrashed: () => request<any>('/banks?trashed=1'),
  create: (data: Partial<import('../types').BankAccount>) =>
    request('/banks', { method: 'POST', body: JSON.stringify(data) }),
  update: (data: Partial<import('../types').BankAccount> & { updatedAt?: string }) =>
    request<{ ok: boolean; updatedAt?: string }>('/banks', { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string) =>
    request('/banks', { method: 'DELETE', body: JSON.stringify({ id }) }),
  restore: (id: string) =>
    request('/banks?action=restore', { method: 'POST', body: JSON.stringify({ id }) }),
  updateBalance: (data: { accountId: string; date: string; balance: number }) =>
    request('/banks', { method: 'POST', body: JSON.stringify({ type: 'balance', ...data }) }),
  deleteBalance: (data: { accountId: string; date: string }) =>
    request('/banks', { method: 'DELETE', body: JSON.stringify({ type: 'balance', ...data }) }),
}

// === 設定 ===
export const settings = {
  list: () => request<import('../types').SettingsData>('/settings'),
  update: (key: string, value: string) =>
    request('/settings', { method: 'POST', body: JSON.stringify({ type: 'setting', key, value }) }),
  setOpeningBalance: (date: string, amount: number) =>
    request('/settings', { method: 'POST', body: JSON.stringify({ type: 'opening_balance', date, amount }) }),
  setFiscalYear: (data: Partial<import('../types').FiscalYear>) =>
    request('/settings', { method: 'POST', body: JSON.stringify({ type: 'fiscal_year', ...data }) }),
}
